<template>
  <BaseResponseComponent :response="response">
    <template #default="{ response: responseData }">
      <div class="attendee-search-results">
        <!-- Search metadata -->
        <div v-if="responseData.metadata" class="search-metadata">
          <div class="metadata-row">
            <span class="metadata-label">Search:</span>
            <span class="metadata-value">{{ responseData.metadata.query }}</span>
          </div>
          <div class="metadata-row">
            <span class="metadata-label">Found:</span>
            <span class="metadata-value">{{ responseData.metadata.total_found }} result{{ responseData.metadata.total_found !== 1 ? 's' : '' }}</span>
            <span v-if="responseData.metadata.search_time_ms" class="search-time">
              ({{ responseData.metadata.search_time_ms }}ms)
            </span>
          </div>
          <div v-if="responseData.metadata.filters_applied && responseData.metadata.filters_applied.length > 0" class="metadata-row">
            <span class="metadata-label">Filters:</span>
            <span class="metadata-value">{{ responseData.metadata.filters_applied.join(', ') }}</span>
          </div>
        </div>

        <!-- Results list -->
        <div v-if="responseData.results && responseData.results.length > 0" class="results-container">
          <div class="results-header">
            <h4>Search Results</h4>
            <div v-if="responseData.ui_suggestions" class="display-options">
              <span class="display-type">{{ getDisplayTypeLabel(responseData.ui_suggestions.display_type) }}</span>
            </div>
          </div>
          
          <div class="results-list" :class="getDisplayClass(responseData.ui_suggestions)">
            <AttendeeCard
              v-for="attendee in responseData.results"
              :key="attendee.id"
              :attendee="attendee"
              :timezone="responseData.metadata?.timezone || 'UTC'"
              @action-click="handleAttendeeAction"
              @session-click="handleSessionClick"
            />
          </div>
        </div>

        <!-- No results message -->
        <div v-else-if="responseData.metadata && responseData.metadata.total_found === 0" class="no-results">
          <div class="no-results-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
              <line x1="11" y1="8" x2="11" y2="12"></line>
              <line x1="11" y1="16" x2="11.01" y2="16"></line>
            </svg>
          </div>
          <h4>No results found</h4>
          <p>Try searching with different keywords or check the spelling.</p>
        </div>

        <!-- UI suggestions for follow-up actions -->
        <div v-if="responseData.ui_suggestions && responseData.ui_suggestions.default_actions" class="follow-up-actions">
          <div class="follow-up-label">You can also:</div>
          <div class="default-actions">
            <button
              v-for="action in responseData.ui_suggestions.default_actions"
              :key="action"
              @click="handleDefaultAction(action)"
              class="default-action-btn"
            >
              {{ getActionLabel(action) }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </BaseResponseComponent>
</template>

<script>
import BaseResponseComponent from './BaseResponseComponent.vue';
import AttendeeCard from './AttendeeCard.vue';

export default {
  name: 'AttendeeSearchResults',
  components: {
    BaseResponseComponent,
    AttendeeCard
  },
  props: {
    response: {
      type: Object,
      required: true
    }
  },
  emits: ['action-click', 'attendee-action', 'session-click'],
  methods: {
    getDisplayTypeLabel(displayType) {
      const typeLabels = {
        'contact_grid': 'Grid View',
        'contact_list': 'List View',
        'speaker_showcase': 'Speaker Showcase'
      };
      return typeLabels[displayType] || 'Results';
    },
    
    getDisplayClass(uiSuggestions) {
      if (!uiSuggestions) return 'display-list';
      
      const displayType = uiSuggestions.display_type || 'contact_list';
      return `display-${displayType.replace('_', '-')}`;
    },
    
    getActionLabel(action) {
      const actionLabels = {
        'view_profile': 'View Profile',
        'send_message': 'Send Message',
        'add_connection': 'Add Connection',
        'view_sessions': 'View Sessions',
        'schedule_meeting': 'Schedule Meeting'
      };
      return actionLabels[action] || action.replace('_', ' ');
    },
    
    handleAttendeeAction(actionData) {
      this.$emit('attendee-action', actionData);
    },
    
    handleSessionClick(sessionData) {
      this.$emit('session-click', sessionData);
    },
    
    handleDefaultAction(action) {
      this.$emit('action-click', {
        action_type: action,
        label: this.getActionLabel(action),
        quick_fill: this.getDefaultActionQuickFill(action)
      });
    },
    
    getDefaultActionQuickFill(action) {
      const quickFills = {
        'view_profile': 'show me profile details',
        'send_message': 'send a message',
        'add_connection': 'connect with attendees',
        'view_sessions': 'show sessions',
        'schedule_meeting': 'schedule a meeting'
      };
      return quickFills[action] || '';
    }
  }
};
</script>

<style scoped>
.attendee-search-results {
  width: 100%;
}

.search-metadata {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 0.85rem;
}

.metadata-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.metadata-row:last-child {
  margin-bottom: 0;
}

.metadata-label {
  font-weight: 600;
  color: #495057;
  margin-right: 8px;
  min-width: 50px;
}

.metadata-value {
  color: #212529;
  font-weight: 500;
}

.search-time {
  color: #6c757d;
  font-size: 0.8rem;
  margin-left: 4px;
}

.results-container {
  margin-bottom: 16px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.results-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
}

.display-options {
  font-size: 0.8rem;
  color: #6c757d;
}

.display-type {
  font-weight: 500;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Grid display for contact_grid */
.results-list.display-contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

/* List display (default) */
.results-list.display-contact-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Speaker showcase display */
.results-list.display-speaker-showcase {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.no-results {
  text-align: center;
  padding: 24px;
  color: #6c757d;
}

.no-results-icon {
  margin-bottom: 12px;
  color: #adb5bd;
}

.no-results h4 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  color: #495057;
}

.no-results p {
  margin: 0;
  font-size: 0.9rem;
}

.follow-up-actions {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.follow-up-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.default-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.default-action-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  font-size: 0.8rem;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  font-weight: 500;
}

.default-action-btn:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #212529;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .results-list.display-contact-grid {
    grid-template-columns: 1fr;
  }
  
  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .search-metadata {
    padding: 10px;
  }
  
  .metadata-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .metadata-label {
    min-width: auto;
    margin-right: 0;
    margin-bottom: 2px;
  }
}

@media (max-width: 480px) {
  .default-actions {
    flex-direction: column;
  }
  
  .default-action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>